import { DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate } from '../fetch';
import { User } from '../../store/auth';
import { CategoryShort } from './categories';
import { SubCategory } from './subCategories';
import { Article } from '../../store/article';

export enum CommentStatus {
  PUBLISHED = 'published',
  DEACTIVATED = 'deactivated',
  DRAFT = 'draft',
}

export enum CommentType {
  BUSINESS = 'business',
  ARTICLE = 'article',
  INTERNATIONAL_COMMUNITY = 'international_community',
  CATEGORY = 'category',
  SUBCATEGORY = 'subcategory',
}

export interface CommentData {
  id: string;
  parentComment: string | null;
  text: string;
  numberOfLikes: number;
  numberOfDislikes: number;
  status?: CommentStatus;
  files: {
    id: string;
    originalName: string;
    location: string;
    mimeType: string;
    createdAt: string;
  }[],
  user: User,
  numberOfComments: number;
  numberOfCommentsTotal: number;
  category: CategoryShort,
  subCategory: SubCategory,
  article: Article,
  createdAt: string;
  updatedAt: string;
}

export enum CommentOrderColumn {
  ID = 'id',
  LIKE = 'numberOfLikes',
  DISLIKE = 'numberOfDislikes',
  NUMBER_OF_COMMENTS = 'numberOfComments',
  CREATED = 'createdAt',
  UPDATED = 'updatedAt',
}

export interface CommentsGetParams extends PagingParams {
  orderByColumn?: CommentOrderColumn;
  postId?: string;
}

export const useCommentsGet = (): FetchGet<
  PagingDataResponse<CommentData>,
  CommentsGetParams
> => useFetchGet(
  'admin/comments',
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export const useCommentChildrenListGet = (id: string): FetchGet<
  PagingDataResponse<CommentData>,
  CommentsGetParams
> => useFetchGet(
  `admin/comments/${id}/children`,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

// export const useCommentsForSelectGet = (): FetchGet<
//   Pick<CommentShort, 'id'>[],
//   CommentsGetParams
// > => useFetchGet(
//   'comments/for-select',
//   {
//     autoStart: false,
//     startStateLoading: false,
//   },
// );

export interface CommentById extends CommentData {
  comments: CommentData[];
}

export const useCommentGetById = (commentId?: string): FetchGet<
  CommentById
> => useFetchGetId(
  'admin/comments',
  commentId,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export interface CommentCreateResponse {
  isActive: boolean;
  name: string;
  header: string;
  description: string;
  fileId: string;
  author: string;
}

export interface CommentCreateData {
  text: string,
  status: CommentStatus,
  entity: CommentType,
  businessId?: string;
  categoryId?: string;
  subCategoryId?: string;
  articleId?: string;
  fileIds?: string[]
}

export const useCommentCreate = (): FetchCreate<
  CommentCreateResponse,
  DefaultFetchError,
  CommentCreateData
> => useFetchCreate(
  'admin/comments',
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export type CommentUpdateResponse = CommentCreateResponse;

export interface CommentUpdateData extends Partial<CommentCreateData> {
  id?: string;
}

export const useCommentUpdate = (commentId?: string): FetchUpdate<
  CommentUpdateResponse,
  DefaultFetchError,
  CommentUpdateData
> => useFetchUpdate(
  'admin/comments',
  commentId,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export const useCommentDelete = (commentId?: string): FetchDelete => useFetchDelete(
  'admin/comments',
  commentId,
  {
    autoStart: false,
    startStateLoading: false,
  },
);

export default {
  useCommentsGet,
  useCommentGetById,
  useCommentCreate,
  useCommentUpdate,
  useCommentDelete,
};
